import React from 'react'
import { View, Text, Icon, Modal } from '@/components'
import { createStyles } from '@codeleap/styles'
import { Chat } from '@/config'
import { Channel } from 'stream-chat'
import { ChatLocalMesage } from '@packages/chat'
import { Getstream } from '@/services'
import { PropsOf } from '@codeleap/types'
import { chatPreviewText, attachmentPreviewText } from '@packages/chat'

export type PinnedBannerModalProps = PropsOf<typeof Modal> & {
  channel: Channel
  message: ChatLocalMesage
}

export const PinnedBannerModal = (props: PinnedBannerModalProps) => {
  const { channel, message, ...modalProps } = props
  const { isGroup, isAdmin } = Getstream.Chat.useMyMembership(channel)
  const { openPinnedMessage } = Getstream.Chat.usePinnedMessageNavigation()

  const attachment = message?.attachments?.[0]
  const hasText = !!message?.text?.trim()

  const previewText = hasText
    ? chatPreviewText({
      message,
      channel,
      isMyMessage: false,
      userName: message?.user?.name,
    })
    : attachmentPreviewText(attachment)

  const handleUnpin = async () => {
    props.toggle?.()
    await Chat.Services.togglePinMessage({
      channel,
      message,
    })
  }

  const handleGoToMessage = () => {
    props.toggle?.()
    openPinnedMessage(message)
  }

  const actions = [{
    id: 'go-to-message',
    label: 'Go to message',
    icon: 'arrow-right',
    onPress: handleGoToMessage,
  }]

  if (!isGroup || isAdmin) {
    actions.push({
      id: 'unpin',
      label: 'Unpin',
      icon: 'pin-blocked-02',
      onPress: handleUnpin,
    })
  }

  const headerModalText = () => {
    return (
      <View style={styles.titleWrapper}>
        <View style={[styles.iconWrapper]}>
          <Icon name='pin-01' size={24} style={[styles.icon]} />
        </View>
        <Text
          text={`${message?.user?.name}: ${previewText}` || 'No preview available'}
          style={styles.messagePreview}
        />
      </View>
    )
  }

  return (
    <Modal
      title={headerModalText()}
      style={['center', 'box:noPadding', { box: styles.box }]}
      showClose
      {...modalProps}
    >
      <View style={styles.wrapper}>
        <View style={styles.content}>
          <View style={styles.actionButton}
          >
            {actions.map((action, index) => (
              <React.Fragment key={action.id}>
                <View
                  style={styles.actionButtonContent}
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onPress()
                  }}
                >
                  <Text text={action.label} style={styles.actionText} />
                  <Icon name={action.icon as any} style={styles.actionIcon} />
                </View>
                {index < actions.length - 1 && (
                  <View style={styles.separator} />
                )}
              </React.Fragment>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  )
}

const styles = createStyles(theme => ({
  titleWrapper: {
    paddingTop: theme.spacing.value(2),
    paddingRight: theme.spacing.value(2),
    width: '90%',
    maxWidth: '90%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.value(2),
  },
  wrapper: {
    ...theme.presets.column,
    width: '100%',
    borderTop: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
    paddingTop: theme.spacing.value(2),
    backgroundColor: theme.colors.buttonNeutralSecondaryBgDefault,

  },
  content: {
    ...theme.presets.column,
    ...theme.spacing.gap(2),
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingBottom(2),
  },
  actionButton: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.baseColors.neutralSolid0,
    borderRadius: theme.radius.regular,
    overflow: 'hidden',
  },
  actionButtonContent: {
    ...theme.presets.row,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.value(1),
    padding: `${theme.spacing.value(1.5)}px ${theme.spacing.value(2)}px`,
    width: '100%',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  actionsContainer: {
    ...theme.presets.row,
    ...theme.spacing.paddingVertical(1),
    ...theme.spacing.paddingHorizontal(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  actionText: {
    fontSize: 14,
    color: theme.colors.textHeadingRegular,
  },
  actionIcon: {
    ...theme.sized(2.5),
    color: theme.colors.textHeadingRegular,
  },
  box: {
    width: '100%',
    maxWidth: '90%',

  },
  container: {
    backgroundColor: theme.colors.bgDefault,
    borderRadius: theme.radius.large,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...theme.spacing.paddingVertical(2),
    ...theme.spacing.paddingHorizontal(2.5),
    gap: theme.spacing.value(1.5),
    borderBottom: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
  },
  pinIcon: {
    fontSize: 18,
    color: theme.baseColors.neutralSolid500,
    flexShrink: 0,
  },
  messagePreview: {
    fontSize: 14,
    color: theme.colors.textHeadingRegular,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    display: 'block',
  },
  closeButton: {
    width: 24,
    height: 24,
    ...theme.presets.center,
    cursor: 'pointer',
    borderRadius: theme.radius.small,
    flexShrink: 0,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  closeIcon: {
    fontSize: 18,
    color: theme.baseColors.neutralSolid500,
  },
  iconWrapper: {
    ...theme.presets.center,
    width: 40,
    height: 40,
    backgroundColor: theme.colors.buttonNeutralSecondaryBgDisabled,
    borderRadius: theme.radius.small,
    padding: 0,
  },
  icon: {
    fontSize: 18,
    color: theme.baseColors.neutralSolid500,
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
    ...theme.spacing.marginVertical(1),
  },
}))
